import { useMemo } from "react";

import { browserName, browserVersion, isAndroid } from "react-device-detect";

import supportedDashboardBrowsers from "../../../public/supported-dashboard-browsers.json";
import supportedFormsBrowsers from "../../../public/supported-forms-browsers.json";

import { SupportedBrowser } from "@/features/UnsupportedBrowser/all-supported-browsers";

const getUserAgent = () => {
  if (typeof window === "undefined") {
    return undefined;
  }
  return window.navigator?.userAgent;
};

function isInAppBrowser(browserName?: string): boolean {
  if (!browserName) {
    return false;
  }

  return ["Facebook", "Instagram", "WhatsApp", "Twitter", "LinkedIn"].includes(browserName);
}

/**
 * Detects the underlying browser when in an in-app browser (Facebook, Instagram, etc.)
 * by parsing the user agent string to find the actual browser engine.
 */
export function detectUnderlyingBrowser(userAgent?: string): { name: string; version: string } | null {
  if (!userAgent) {
    return null;
  }

  // Facebook in-app browser patterns
  if (userAgent.includes("FBAN") || userAgent.includes("FBAV")) {
    // Look for Chrome/WebKit version in Facebook's user agent
    const chromeMatch = userAgent.match(/Chrome\/(\d+\.\d+)/);
    if (chromeMatch) {
      return { name: "Chrome", version: chromeMatch[1] };
    }

    // Look for Safari version in Facebook's user agent
    const safariMatch = userAgent.match(/Version\/(\d+\.\d+)/);
    if (safariMatch) {
      return { name: "Safari", version: safariMatch[1] };
    }
  }

  // Instagram in-app browser patterns
  if (userAgent.includes("Instagram")) {
    // Instagram uses WebKit, look for Chrome version
    const chromeMatch = userAgent.match(/Chrome\/(\d+\.\d+)/);
    if (chromeMatch) {
      return { name: "Chrome", version: chromeMatch[1] };
    }

    // Look for Safari version
    const safariMatch = userAgent.match(/Version\/(\d+\.\d+)/);
    if (safariMatch) {
      return { name: "Safari", version: safariMatch[1] };
    }
  }

  // WhatsApp in-app browser patterns
  if (userAgent.includes("WhatsApp")) {
    const chromeMatch = userAgent.match(/Chrome\/(\d+\.\d+)/);
    if (chromeMatch) {
      return { name: "Chrome", version: chromeMatch[1] };
    }
  }

  // Twitter/X in-app browser patterns
  if (userAgent.includes("Twitter") || userAgent.includes("X-Client")) {
    const chromeMatch = userAgent.match(/Chrome\/(\d+\.\d+)/);
    if (chromeMatch) {
      return { name: "Chrome", version: chromeMatch[1] };
    }
  }

  // LinkedIn in-app browser patterns
  if (userAgent.includes("LinkedInApp")) {
    const chromeMatch = userAgent.match(/Chrome\/(\d+\.\d+)/);
    if (chromeMatch) {
      return { name: "Chrome", version: chromeMatch[1] };
    }
  }

  return null;
}

type GetCurrentBrowserParams = {
  browserName?: string;
  browserVersion?: string;
  isAndroid?: boolean;
  userAgent?: string;
};

export function getCurrentBrowser({
  browserName,
  browserVersion,
  isAndroid,
  userAgent,
}: GetCurrentBrowserParams): { browserKey: SupportedBrowser; browserVersion?: string } | null {
  if (isInAppBrowser(browserName)) {
    const underlyingBrowser = detectUnderlyingBrowser(userAgent);

    if (underlyingBrowser) {
      // Recursively call with the detected underlying browser
      return getCurrentBrowser({
        browserName: underlyingBrowser.name,
        browserVersion: underlyingBrowser.version,
        isAndroid,
        userAgent,
      });
    }
    // If we can't detect the underlying browser, treat as unsupported
    return null;
  }

  if (browserName === "Chrome") {
    return { browserKey: isAndroid ? "and_chr" : "chrome", browserVersion };
  }

  if (browserName === "Edge") {
    return { browserKey: "edge", browserVersion };
  }

  if (browserName === "Firefox") {
    return { browserKey: isAndroid ? "and_ff" : "firefox", browserVersion };
  }

  if (browserName === "Safari") {
    return { browserKey: "safari", browserVersion };
  }

  if (browserName === "Samsung Browser") {
    return { browserKey: "samsung", browserVersion };
  }

  if (browserName === "Android Browser") {
    return { browserKey: "android", browserVersion };
  }

  if (browserName === "Mobile Safari") {
    return { browserKey: "ios_saf", browserVersion };
  }

  return null;
}

type IsBrowserSupportedParams = {
  browserName?: string;
  browserVersion?: string;
  supportedBrowsers: Record<string, number[]>;
  isAndroid?: boolean;
  userAgent?: string;
};

export const isBrowserSupported = ({
  browserName,
  browserVersion,
  supportedBrowsers,
  isAndroid,
  userAgent,
}: IsBrowserSupportedParams): boolean => {
  if (!browserName || !browserVersion) {
    return false;
  }

  if (browserName === "GSA") {
    // Let search engines pass through
    return true;
  }

  const browser = getCurrentBrowser({ browserName, browserVersion, isAndroid, userAgent });

  if (!browser) {
    return false;
  }

  const supportedVersions = supportedBrowsers[browser.browserKey];
  if (!supportedVersions || supportedVersions.length === 0) {
    return false;
  }

  // Extract major version number from browser version string
  const majorVersion = parseInt(browser.browserVersion?.split(".")[0] ?? "unknown", 10);
  if (isNaN(majorVersion)) {
    return false;
  }

  const minimalSupportedVersion = supportedVersions[0];

  return majorVersion >= minimalSupportedVersion;
};

export const useIsUnsupportedBrowserForDashboard = () => {
  return useMemo(() => {
    return !isBrowserSupported({
      browserName,
      browserVersion,
      supportedBrowsers: supportedDashboardBrowsers,
      isAndroid,
      userAgent: getUserAgent(),
    });
  }, []);
};

export const useIsUnsupportedBrowserForForms = () => {
  return useMemo(() => {
    return !isBrowserSupported({
      browserName,
      browserVersion,
      supportedBrowsers: supportedFormsBrowsers,
      isAndroid,
      userAgent: getUserAgent(),
    });
  }, []);
};
